
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Check, X } from 'lucide-react';
import { BRAND_CONFIG } from '../config/brand';
import { LandingHeader } from './landing/LandingHeader';
import { LandingFooter } from './landing/LandingFooter';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { PlanId } from '../config/plans';

const PLAN_ORDER: Record<PlanId, number> = { free: 0, plus: 1, premium: 2 };

interface PlanButtonProps {
  planId: PlanId;
  currentPlanId: PlanId;
  isLoggedIn: boolean;
}

const PlanButton: React.FC<PlanButtonProps> = ({ planId, currentPlanId, isLoggedIn }) => {
  if (!isLoggedIn) {
    if (planId === 'free') {
      return (
        <Button asChild className="w-full bg-wedding-pink hover:bg-wedding-pink/90 text-white">
          <Link to="/register">免費建立邀請函</Link>
        </Button>
      );
    }
    if (planId === 'plus') {
      return (
        <Button
          className="w-full bg-wedding-gold text-white opacity-75 cursor-not-allowed"
          disabled
        >
          即將推出
        </Button>
      );
    }
    return (
      <Button
        className="w-full bg-gradient-to-r from-wedding-gold to-yellow-500 text-white opacity-75 cursor-not-allowed"
        disabled
      >
        即將推出
      </Button>
    );
  }

  if (currentPlanId === planId) {
    return (
      <Button
        variant="outline"
        className="w-full border-wedding-pink text-wedding-pink cursor-not-allowed"
        disabled
      >
        目前方案
      </Button>
    );
  }

  if (PLAN_ORDER[currentPlanId] > PLAN_ORDER[planId]) {
    return (
      <Button
        variant="outline"
        className="w-full border-gray-300 text-gray-400 cursor-not-allowed"
        disabled
      >
        已包含
      </Button>
    );
  }

  if (planId === 'plus') {
    return (
      <Button
        className="w-full bg-wedding-gold text-white opacity-75 cursor-not-allowed"
        disabled
      >
        即將推出
      </Button>
    );
  }

  return (
    <Button
      className="w-full bg-gradient-to-r from-wedding-gold to-yellow-500 text-white opacity-75 cursor-not-allowed"
      disabled
    >
      即將推出
    </Button>
  );
};

const FAQ_ITEMS = [
  {
    question: '可以隨時取消訂閱嗎？',
    answer: '可以，您隨時都能取消訂閱，不會有額外的取消費用。取消後，您的帳號會在當期結束後自動降回免費方案，已建立的資料都會保留。',
  },
  {
    question: '免費版有什麼限制？',
    answer: '免費版包含所有核心規劃功能，但限制最多 1 張邀請函、30 位賓客和 3 桌座位安排。適合小型婚禮或是先體驗看看平台功能。',
  },
  {
    question: '婚禮結束後資料還在嗎？',
    answer: '所有資料都會完整保留，不會因為婚禮結束而消失。您可以隨時回來查看賓客名單、紅包紀錄等珍貴資料。',
  },
  {
    question: '可以先用免費版再升級嗎？',
    answer: '當然可以！我們建議您先用免費版體驗核心功能，等確認需要更多賓客名額或進階功能時再升級，所有資料會無縫銜接。',
  },
  {
    question: '付款方式有哪些？',
    answer: '我們支援信用卡和簽帳金融卡付款，透過安全的第三方支付平台處理，您的付款資訊不會經過我們的伺服器。',
  },
];

const PricingPage: React.FC = () => {
  const { user } = useAuth();
  const { planId: currentPlanId } = useSubscription();
  const isLoggedIn = !!user;

  return (
    <div className="flex flex-col min-h-screen bg-wedding-bg text-wedding-text-body">
      <LandingHeader
        variant="A"
        brandName={BRAND_CONFIG.name}
      />

      <main className="flex-1">
        <section id="pricing" className="w-full py-12 md:py-16 lg:py-20">
          <div className="container mx-auto px-4 md:px-6">
            <div className="mx-auto mb-12 max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl text-wedding-text-title">選擇適合您的方案</h2>
              <p className="mt-4">花費不到一頓雙人晚餐的價格，為您一生一次的大事，省下數十小時的煩惱。</p>
            </div>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
              {/* Free Plan */}
              <Card className="border-gray-200 shadow-md flex flex-col">
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-2xl text-wedding-text-title">溫馨方案</CardTitle>
                  <CardDescription>適合小型婚禮或體驗</CardDescription>
                  <p className="text-4xl font-bold text-wedding-text-title mt-4">免費</p>
                </CardHeader>
                <CardContent className="flex flex-col gap-4 flex-grow">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-500" />
                      <span>所有核心規劃功能</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-500" />
                      <span>最多 1 張邀請函</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-500" />
                      <span>最多 30 位賓客</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-500" />
                      <span>最多 3 桌座位安排</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <X className="h-5 w-5 text-red-500" />
                      <span>匯出賓客名單與郵寄標籤</span>
                    </li>
                  </ul>
                </CardContent>
                <div className="p-6 pt-0">
                  <PlanButton planId="free" currentPlanId={currentPlanId} isLoggedIn={isLoggedIn} />
                </div>
              </Card>
              {/* Plus Plan — Featured */}
              <div className="relative group lg:scale-103">
                <div className="absolute -inset-1 bg-gradient-to-r from-wedding-pink to-wedding-gold rounded-lg blur opacity-60 group-hover:opacity-80 transition duration-300"></div>
                <Card className="relative border-wedding-gold shadow-2xl flex flex-col h-full">
                  <div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2">
                      <div className="bg-wedding-gold text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg">最受歡迎</div>
                  </div>
                  <CardHeader className="text-center pb-4">
                    <CardTitle className="text-2xl text-wedding-text-title">標準方案</CardTitle>
                    <CardDescription>適合絕大多數婚禮</CardDescription>
                    <p className="text-4xl font-bold text-wedding-text-title mt-4">NT$ 299</p>
                    <p className="text-sm">每月訂閱</p>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-4 flex-grow">
                    <ul className="space-y-3">
                      <li className="flex items-center gap-2">
                        <Check className="h-5 w-5 text-green-500" />
                        <span>所有核心規劃功能</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-5 w-5 text-green-500" />
                        <span>最多 3 張邀請函</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-5 w-5 text-green-500" />
                        <span>最多 500 位賓客</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-5 w-5 text-green-500" />
                        <span>最多 50 桌座位安排</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-5 w-5 text-green-500" />
                        <span>匯出賓客名單與郵寄標籤</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-5 w-5 text-green-500" />
                        <span>多用戶協作</span>
                      </li>
                    </ul>
                  </CardContent>
                  <div className="p-6 pt-0">
                    <PlanButton planId="plus" currentPlanId={currentPlanId} isLoggedIn={isLoggedIn} />
                  </div>
                </Card>
              </div>
              {/* Premium Plan */}
              <Card className="border-gray-200 shadow-md flex flex-col relative">
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-2xl text-wedding-text-title">進階方案</CardTitle>
                  <CardDescription>適合婚顧團隊或是大型婚禮</CardDescription>
                  <p className="text-4xl font-bold text-wedding-text-title mt-4">NT$ 499</p>
                  <p className="text-sm">每月訂閱</p>
                </CardHeader>
                <CardContent className="flex flex-col gap-4 flex-grow">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-500" />
                      <span>所有核心規劃功能</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-500" />
                      <span>最多 10 張邀請函</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-500" />
                      <span className="font-semibold">無限賓客數量</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-500" />
                      <span className="font-semibold">無限桌數安排</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-500" />
                      <span>完整匯出功能</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-500" />
                      <span className="font-semibold text-wedding-gold">多用戶協作（不限人數）</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-500" />
                      <span>自訂品牌文字</span>
                    </li>
                  </ul>
                </CardContent>
                <div className="p-6 pt-0">
                  <PlanButton planId="premium" currentPlanId={currentPlanId} isLoggedIn={isLoggedIn} />
                </div>
              </Card>
            </div>

            {/* FAQ Section */}
            <div className="max-w-3xl mx-auto mt-16 md:mt-20">
              <h3 className="text-2xl font-bold text-center text-wedding-text-title mb-8">常見問題</h3>
              <Accordion type="single" collapsible className="w-full">
                {FAQ_ITEMS.map((item, index) => (
                  <AccordionItem key={index} value={`faq-${index}`}>
                    <AccordionTrigger className="text-left text-base font-medium">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-wedding-text-body">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter
        brandName={BRAND_CONFIG.name}
        layout="simple"
      />
    </div>
  );
};

export default PricingPage;
